import { API_URL } from './config';
import { stampMedia } from './media';

export interface ApiErrorShape {
  code: string;
  message: string;
  details?: unknown;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

const ACCESS_KEY = 'itsappy.accessToken';
const REFRESH_KEY = 'itsappy.refreshToken';
const ORG_KEY = 'itsappy.orgId';
const MEDIA_KEY = 'itsappy.mediaToken';

export const tokenStore = {
  get access() {
    return localStorage.getItem(ACCESS_KEY);
  },
  get refresh() {
    return localStorage.getItem(REFRESH_KEY);
  },
  get orgId() {
    return localStorage.getItem(ORG_KEY);
  },
  get media() {
    return localStorage.getItem(MEDIA_KEY);
  },
  set(tokens: { accessToken: string; refreshToken?: string; mediaToken?: string }) {
    localStorage.setItem(ACCESS_KEY, tokens.accessToken);
    if (tokens.refreshToken) localStorage.setItem(REFRESH_KEY, tokens.refreshToken);
    if (tokens.mediaToken) localStorage.setItem(MEDIA_KEY, tokens.mediaToken);
  },
  setMedia(mediaToken: string) {
    localStorage.setItem(MEDIA_KEY, mediaToken);
  },
  setOrg(orgId: string) {
    localStorage.setItem(ORG_KEY, orgId);
  },
  clear() {
    vaultToken = null;
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
    localStorage.removeItem(ORG_KEY);
    localStorage.removeItem(MEDIA_KEY);
  },
};

/**
 * The vault pass, if the PIN has been typed recently.
 *
 * Held in memory rather than in `localStorage`, which the rest of this module
 * uses: a vault that survived a closed tab would be a vault that anyone who
 * reopens the browser walks into. Attached to every request the way the
 * workspace id is — the server only reads it on the routes into the vault.
 */
let vaultToken: string | null = null;

export const vaultPass = {
  get token() {
    return vaultToken;
  },
  set(token: string | null) {
    vaultToken = token;
  },
};

type Listener = () => void;
const unauthorizedListeners = new Set<Listener>();

export function onUnauthorized(listener: Listener): () => void {
  unauthorizedListeners.add(listener);
  return () => unauthorizedListeners.delete(listener);
}

/**
 * Concurrent 401s share one refresh round trip — otherwise a page that fires
 * six queries at mount would rotate the refresh token six times and lose.
 */
let refreshInFlight: Promise<boolean> | null = null;

async function refreshAccessToken(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    const refreshToken = tokenStore.refresh;
    if (!refreshToken) return false;
    try {
      const res = await fetch(`${API_URL}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });
      if (!res.ok) return false;
      const data = (await res.json()) as {
        accessToken: string;
        refreshToken: string;
        mediaToken?: string;
      };
      tokenStore.set(data);
      return true;
    } catch {
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

/**
 * Returns an access token that will remain valid long enough to complete a
 * realtime handshake. EasyRTC does not go through `request`, so it cannot use
 * the normal 401-and-retry path when the stored JWT has expired.
 */
export async function getValidAccessToken(): Promise<string | null> {
  const accessToken = tokenStore.access;
  if (!accessToken) return null;

  let expiresAt = 0;
  try {
    const payload = accessToken.split('.')[1];
    if (payload) {
      const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
      const decoded = JSON.parse(atob(normalized)) as { exp?: number };
      expiresAt = typeof decoded.exp === 'number' ? decoded.exp * 1000 : 0;
    }
  } catch {
    // A malformed token cannot authenticate; let the refresh flow replace it.
  }

  // Leave enough margin for loading the client and completing the socket
  // handshake instead of sending a token that expires while connecting.
  if (expiresAt > Date.now() + 60_000) return accessToken;
  if (await refreshAccessToken()) return tokenStore.access;

  tokenStore.clear();
  for (const listener of unauthorizedListeners) listener();
  return null;
}

export interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  /** Skips JSON encoding — used for multipart uploads. */
  raw?: boolean;
  skipAuthRetry?: boolean;
  /** Do not attach workspace credentials (public guest and registration flows). */
  anonymous?: boolean;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, raw, skipAuthRetry, anonymous, headers, ...rest } = options;

  const finalHeaders = new Headers(headers);
  if (!anonymous) {
    const access = tokenStore.access;
    if (access) finalHeaders.set('Authorization', `Bearer ${access}`);
    const orgId = tokenStore.orgId;
    if (orgId) finalHeaders.set('x-org-id', orgId);
    if (vaultToken) finalHeaders.set('x-vault-token', vaultToken);
  }
  if (body !== undefined && !raw) finalHeaders.set('Content-Type', 'application/json');

  const res = await fetch(`${API_URL}${path.startsWith('/api') ? '' : '/api'}${path}`, {
    ...rest,
    headers: finalHeaders,
    body: raw ? (body as BodyInit) : body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (res.status === 401 && !skipAuthRetry) {
    if (await refreshAccessToken()) {
      return request<T>(path, { ...options, skipAuthRetry: true });
    }
    tokenStore.clear();
    for (const listener of unauthorizedListeners) listener();
    throw new ApiError(401, 'unauthorized', 'Your session has expired — please sign in again');
  }

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  // Every upload URL in the payload gets the media pass stamped on here, so
  // that nothing downstream has to know `/uploads` asks for a credential.
  const data = text ? stampMedia(JSON.parse(text) as unknown) : null;

  if (!res.ok) {
    const err = (data as { error?: ApiErrorShape } | null)?.error;
    throw new ApiError(
      res.status,
      err?.code ?? 'error',
      err?.message ?? `Request failed with ${res.status}`,
      err?.details,
    );
  }

  return data as T;
}

export const api = {
  get: <T>(path: string, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'POST', body }),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'PUT', body }),
  delete: <T>(path: string, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'DELETE' }),
  upload: <T>(path: string, formData: FormData) =>
    request<T>(path, { method: 'POST', body: formData, raw: true }),
};
